/**
 * @file Hook de détection de l'état de connexion réseau du navigateur.
 * @module packages.common.hooks.useNetworkStatus
 *
 * S'appuie sur `navigator.onLine` et les événements `online`/`offline` de la fenêtre.
 * Fonctionne identiquement en mode PWA standalone et en mode navigateur classique.
 *
 * Limite connue de l'API navigateur : `navigator.onLine` reporte l'état de la couche
 * réseau, pas la véritable connectivité internet. Un utilisateur connecté au wifi
 * mais sans accès sortant (routeur en panne, portail captif) sera vu comme "online".
 * Pour couvrir ce cas, ajouter un heartbeat vers le backend (non implémenté ici).
 */

import {useEffect, useState} from 'react';

/**
 * Détecte l'état de connexion du navigateur en temps réel.
 *
 * @return {{isOnline: boolean}} État courant de la connexion.
 *
 * @example
 * const {isOnline} = useNetworkStatus();
 * if (!isOnline) return <OfflineWarning />;
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {isOnline};
};

export default useNetworkStatus;
